/**
 * Just enough markdown for a trainer's answer, and deliberately not one byte
 * more.
 *
 * ── WHY NOT A MARKDOWN LIBRARY ──────────────────────────────────────────────
 * The producer is known and finite. `@repo/agents` (`src/core/trainer/prompt.ts`) asks for
 * prose, `validateTrainerAnswer` renders the citations, and the renderer's `sources`
 * writes the block this screen strips before rendering. What actually reaches a
 * bubble is paragraphs, bullets and `**bold**` — three constructs. A general
 * renderer would bring a parser, a component tree and a dependency to the one
 * screen in the app whose text is written by a language model, which is also
 * the one screen where an unexpected construct must not be able to do anything
 * interesting.
 *
 * ── AND WHY THE UNKNOWN CASE IS "SHOW IT AS TEXT" ───────────────────────────
 * Anything this does not recognise is rendered literally, including its
 * markers. That is the safe direction: a stray `#` on screen is untidy, whereas
 * swallowing a line because it looked like syntax would delete a sentence from
 * a training answer and leave no trace that it was there.
 */
/**
 * `**bold**`, and nothing nested inside it.
 *
 * Non-greedy and requiring at least one character between the markers, so a
 * literal `****` is text rather than an empty emphasis. Unpaired markers stay
 * as they are — see the note above on the unknown case.
 */
const BOLD = /\*\*([^*]+)\*\*/g;
export function splitSpans(line) {
    const spans = [];
    let cursor = 0;
    for (const match of line.matchAll(BOLD)) {
        const start = match.index;
        if (start > cursor) {
            spans.push({ bold: false, text: line.slice(cursor, start) });
        }
        spans.push({ bold: true, text: match[1] });
        cursor = start + match[0].length;
    }
    if (cursor < line.length) {
        spans.push({ bold: false, text: line.slice(cursor) });
    }
    return spans.length === 0 ? [{ bold: false, text: line }] : spans;
}
/** `- item` and `* item` at the start of a line, with any leading indent. */
const BULLET = /^\s*[-*]\s+/;
/**
 * A rendered answer, as blocks.
 *
 * Blank lines are dropped rather than turned into empty blocks: the spacing
 * between paragraphs is a style on the block, so an empty one would be a second
 * gap that the first already provides.
 *
 * ── A SINGLE NEWLINE IS A SOFT WRAP, NOT A PARAGRAPH (`T-159`) ──────────────
 * This used to be one block per non-empty LINE, which is a different rule from
 * the one the docblock at the top of this file states — that what reaches a
 * bubble is paragraphs, bullets and `**bold**`. Every producer that wraps its
 * prose was therefore rendered as one paragraph per wrapped line, with a
 * paragraph gap at each wrap point.
 *
 * The one that shipped is `ungroundedMessage` in `@repo/agents`
 * (`core/trainer/prompt.ts`): a template literal wrapped at 80 columns to fit
 * the source file. Three paragraphs came out as SEVEN blocks, splitting two
 * sentences mid-clause and leaving the word "fazer." alone on its own line —
 * on the one screen whose entire job is to look considered rather than broken.
 *
 * So the separator is a BLANK line, and consecutive lines join with a space,
 * exactly as markdown defines it. A bullet marker still starts a block, and a
 * plain line after a bullet continues that bullet rather than becoming a
 * stray paragraph — a wrapped bullet is the same soft wrap one level in.
 */
export function toBlocks(text) {
    const blocks = [];
    let open = null;
    for (const line of text.split("\n")) {
        if (line.trim() === "") {
            open = null;
            continue;
        }
        if (BULLET.test(line)) {
            open = { kind: "bullet", text: line.replace(BULLET, "").trim() };
            blocks.push(open);
            continue;
        }
        if (open === null) {
            open = { kind: "text", text: line.trim() };
            blocks.push(open);
            continue;
        }
        // A continuation of the block above: joined with a space, because the
        // newline was a wrap in the producer's source and not a break it meant.
        open.text = `${open.text} ${line.trim()}`;
    }
    return blocks.map((block) => ({
        kind: block.kind,
        spans: splitSpans(block.text),
    }));
}
//# sourceMappingURL=markdown.js.map